import React from 'react';
import {
  Accordion,
  Stack,
  Typography,
  IconBadgeStatus,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { formatDateDisplayLocal } from '@fortress-technology-solutions/fortress-component-library/utils';
import {
  useScreeningAccordion,
  useScreeningProviders,
  useScreeningStatus,
  useSubmissionData,
  useActiveProviderPropertyProfiles,
  useApplicationError,
} from '../../../../components/CreditCriminalStatusScreening/hooks';
import ScreeningProviderFlow from '../../../../components/CreditCriminalStatusScreening/ScreeningProviderFlow';
import ScreeningErrors from '../../../../components/CreditCriminalStatusScreening/ScreeningErrors';
import messages from '../../../../components/CreditCriminalStatusScreening/messages';

type Props = {
  intl: Object,
  screening: Object,
};

function ScreeningHistoryEntry({ intl, screening }: Props) {
  const accordion = useScreeningAccordion();
  const activeProviderPropertyProfiles = useActiveProviderPropertyProfiles([]);

  const finalDecision = React.useMemo(() => {
    if (!screening?.finalDecisions?.length) return null;
    return [...screening.finalDecisions].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    )[0];
  }, [screening]);

  const screeningProviders = useScreeningProviders({
    householdScreening: screening,
    activeProviderPropertyProfiles,
    applicantsValidationResult: null,
    intl,
    applicationStatus: undefined,
    isResident: true,
  });

  const screeningStatus = useScreeningStatus({
    screeningProviders,
    applicantsValidationResult: null,
    householdScreening: screening,
    finalDecision,
    isResident: true,
  });

  const submissionData = useSubmissionData({
    householdScreening: screening,
    screeningStatus,
  });

  const applicationErrors = useApplicationError({
    intl,
    householdScreening: screening,
  });

  const screeningDate = screening?.createdAt
    ? formatDateDisplayLocal(screening.createdAt)
    : '';
  const purposeName = screening?.screeningPurpose?.name ?? '';
  const accordionLabel = [
    intl.formatMessage(messages.title),
    screeningDate,
    purposeName,
  ]
    .filter(Boolean)
    .join(' - ');

  return (
    <Accordion
      summary={
        <Stack direction="row" spacing={2} alignItems="center" width="100%">
          <IconBadgeStatus variant={screeningStatus} />
          <Typography variant="bodyLarge">{accordionLabel}</Typography>
        </Stack>
      }
      expanded={accordion.expanded}
      onChange={accordion.toggle}
      sx={{ borderBottom: 'none' }}
    >
      <Stack spacing={2}>
        {applicationErrors && (
          <ScreeningErrors intl={intl} {...applicationErrors} />
        )}
        <Stack>
          <ScreeningProviderFlow
            intl={intl}
            providers={screeningProviders}
            screeningStatus={screeningStatus}
          />
          {submissionData && (
            <Typography
              variant="bodySmall"
              color="text.secondary"
              alignSelf="flex-end"
              marginTop={0.5}
            >
              {intl.formatMessage(messages.screeningSubmittedOn)}{' '}
              {submissionData.submittedOn} {intl.formatMessage(messages.by)}{' '}
              {submissionData.submittedBy}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Accordion>
  );
}

export default ScreeningHistoryEntry;
